// @ts-nocheck
import { useState } from 'react';
import {
  ActionIcon,
  Blockquote,
  Button,
  Card,
  Code,
  ColorSchemePreview,
  Divider,
  DomainTile,
  GradientCard,
  Group,
  Icon,
  PushSettings,
  SimpleGrid,
  Stack,
  StatusPill,
  Text,
  Title,
} from '@/components/ui';
import {
  IconInfoCircle,
  IconHeart,
  IconStar,
  IconTrash,
  IconSettings,
} from '@tabler/icons-react';

// These three are not re-exported from the barrel, so import from their files.
import { EmojiStatusPicker } from '@/components/ui/EmojiStatusPicker';
import { IdleTimeDisplay } from '@/components/ui/IdleTimeDisplay';
import { PresenceIndicator } from '@/components/ui/PresenceIndicator';

export default function ExtrasPage() {
  // Self-contained local state for the interactive examples (no backend).
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [status, setStatus] = useState<{ emoji: string; message: string }>({
    emoji: '☕',
    message: 'Taking a coffee break',
  });

  // Inline mock timestamps for IdleTimeDisplay (pure client-side relative time).
  const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
  const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);

  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Title order={2}>
          Extras (Blockquote, GradientCard, StatusPill, ColorSchemePreview, Icon,
          DomainTile, …)
        </Title>
        <Text c="dimmed" size="sm">
          Standalone, project-specific helpers that build on the core kit - gradient
          surfaces, status accents, the central icon registry, and presence/status
          widgets used across CCMS apps
        </Text>
      </Stack>

      {/* Blockquote Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Blockquote</Title>
            <Text size="sm" c="dimmed">
              Themed blockquote with an optional gradient left border and icon
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Examples:</Text>
            <Stack gap="md">
              <Blockquote cite="– Acts Christian Church" icon={<IconInfoCircle />}>
                Serving the community with clarity, consistency, and care across every
                ministry.
              </Blockquote>
              <Blockquote
                gradient
                gradientVariant="primary"
                cite="– Product Team"
                icon={<IconStar />}
              >
                The skeleton kit keeps every CCMS app on the same design language.
              </Blockquote>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Blockquote cite="– Acts Christian Church" icon={<IconInfoCircle />}>
  Serving the community with clarity, consistency, and care.
</Blockquote>

<Blockquote gradient gradientVariant="primary" icon={<IconStar />}>
  The skeleton kit keeps every CCMS app on the same design language.
</Blockquote>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • gradient - boolean to enable the gradient left border
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'tertiary' | 'success' |
              'info' | 'warning' | 'danger'
              <br />
              • cite / icon - standard Mantine Blockquote props
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* GradientCard Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>GradientCard</Title>
            <Text size="sm" c="dimmed">
              Card with a themed gradient background and automatic contrast-aware text
              color
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Variants:</Text>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
              <GradientCard variantName="primary">
                <Text fw={600}>Primary</Text>
                <Text size="sm">Default brand gradient</Text>
              </GradientCard>
              <GradientCard variantName="success">
                <Text fw={600}>Success</Text>
                <Text size="sm">Positive outcomes</Text>
              </GradientCard>
              <GradientCard variantName="danger">
                <Text fw={600}>Danger</Text>
                <Text size="sm">Critical attention</Text>
              </GradientCard>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<GradientCard variantName="primary">
  <Text fw={600}>Primary</Text>
  <Text size="sm">Default brand gradient</Text>
</GradientCard>

<GradientCard variantName="success">…</GradientCard>
<GradientCard variantName="danger">…</GradientCard>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • variantName - 'primary' | 'secondary' | 'tertiary' | 'success' | 'info'
              | 'warning' | 'danger'
              <br />
              • text color is auto-calculated for contrast (override via style.color)
              <br />
              • accepts all Mantine Card props (radius, p, etc.)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* StatusPill Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>StatusPill</Title>
            <Text size="sm" c="dimmed">
              Pill-shaped badge with a gradient fill for compact status labels
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Examples:</Text>
            <Group>
              <StatusPill status="success">Active</StatusPill>
              <StatusPill status="info">In Review</StatusPill>
              <StatusPill status="warning">Pending</StatusPill>
              <StatusPill status="danger">Overdue</StatusPill>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<StatusPill status="success">Active</StatusPill>
<StatusPill status="info">In Review</StatusPill>
<StatusPill status="warning">Pending</StatusPill>
<StatusPill status="danger">Overdue</StatusPill>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • status - 'success' | 'info' | 'warning' | 'danger'
              <br />
              • children - the label content
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* ColorSchemePreview Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ColorSchemePreview</Title>
            <Text size="sm" c="dimmed">
              Swatch preview of a theme color scheme (primary / secondary / tertiary
              gradients) with an optional label
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Examples:</Text>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
              <ColorSchemePreview schemeKey="royal-indigo" />
              <ColorSchemePreview schemeKey="sunset-citrus" />
              <ColorSchemePreview schemeKey="deep-ocean" selected />
            </SimpleGrid>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Sizes:</Text>
            <Group align="flex-end">
              <ColorSchemePreview schemeKey="forest-light" size="xs" showLabel={false} />
              <ColorSchemePreview schemeKey="forest-light" size="sm" showLabel={false} />
              <ColorSchemePreview schemeKey="forest-light" size="md" showLabel={false} />
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<ColorSchemePreview schemeKey="royal-indigo" />
<ColorSchemePreview schemeKey="deep-ocean" selected />
<ColorSchemePreview
  schemeKey="forest-light"
  size="sm"
  showLabel={false}
  onClick={(scheme) => console.log(scheme.key)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • schemeKey - color scheme key (e.g. 'royal-indigo', 'deep-ocean')
              <br />
              • size - 'xs' | 'sm' | 'md' | 'lg'
              <br />
              • showLabel - show the emoji + name caption
              <br />
              • selected - highlight as the active choice
              <br />
              • onClick(scheme) - callback receiving the full ColorScheme
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Icon Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Icon</Title>
            <Text size="sm" c="dimmed">
              Theme-aware icon that resolves names from the central icon registry, with
              size, color, variant, and spin support
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Named icons &amp; sizes:</Text>
            <Group align="center">
              <Icon name="home" size="sm" aria-label="Home" />
              <Icon name="user" size="md" aria-label="User" />
              <Icon name="bell" size="lg" aria-label="Notifications" />
              <Icon name="settings" size="xl" aria-label="Settings" />
            </Group>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <Text size="sm" fw={500}>Variants, color &amp; spin:</Text>
            <Group align="center">
              <Icon name="star" variant="muted" aria-label="Muted star" />
              <Icon name="heart" color="red" aria-label="Favorite" />
              <Icon name="search" rotate={90} aria-label="Rotated search" />
              <Icon name="settings" spin aria-label="Loading settings" />
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Icon name="home" size="sm" aria-label="Home" />
<Icon name="bell" size="lg" aria-label="Notifications" />
<Icon name="heart" color="red" aria-label="Favorite" />
<Icon name="settings" spin aria-label="Loading settings" />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • name - registry key (e.g. 'home', 'bell', 'user', 'settings')
              <br />
              • size - 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number
              <br />
              • color - any CSS color string
              <br />
              • variant - icon style variant (e.g. 'default' | 'muted')
              <br />
              • spin - boolean to animate rotation; rotate - degrees
              <br />
              • aria-label - accessible label (otherwise the icon is presentational)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* DomainTile Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>DomainTile</Title>
            <Text size="sm" c="dimmed">
              Clickable gradient tile for a CCMS domain - shows the domain icon, label,
              and description, and navigates to its base path
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Examples:</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <DomainTile domainKey="projects" />
              <DomainTile domainKey="tasks" />
              <DomainTile domainKey="members" />
              <DomainTile domainKey="meetings" />
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<DomainTile domainKey="projects" />
<DomainTile domainKey="tasks" />
<DomainTile domainKey="members" />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • domainKey - a registered DomainKey (e.g. 'projects', 'tasks', 'members')
              <br />
              • reads label / description / icon / basePath from the DOMAINS config
              <br />
              • clicking navigates to the domain's basePath via react-router
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* ActionIcon Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ActionIcon</Title>
            <Text size="sm" c="dimmed">
              Icon-only button with themed variants, an optional gradient fill, and
              hover effects
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Variants:</Text>
            <Group>
              <ActionIcon variant="subtle" aria-label="Settings" onClick={() => {}}>
                <IconSettings size={18} />
              </ActionIcon>
              <ActionIcon variant="light" aria-label="Favorite" onClick={() => {}}>
                <IconHeart size={18} />
              </ActionIcon>
              <ActionIcon variant="outline" aria-label="Star" onClick={() => {}}>
                <IconStar size={18} />
              </ActionIcon>
              <ActionIcon
                variant="gradient"
                gradientVariant="primary"
                hoverEffect="lift"
                aria-label="Delete"
                onClick={() => {}}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<ActionIcon variant="subtle" aria-label="Settings">
  <IconSettings size={18} />
</ActionIcon>

<ActionIcon
  variant="gradient"
  gradientVariant="primary"
  hoverEffect="lift"
  aria-label="Delete"
>
  <IconTrash size={18} />
</ActionIcon>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • variant - 'gradient' | 'filled' | 'light' | 'outline' | 'subtle' |
              'transparent' | 'default'
              <br />
              • gradientVariant - gradient key when variant="gradient"
              <br />
              • hoverEffect - 'glow' | 'lift' | 'scale' | 'none'
              <br />
              • onClick - click handler
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* EmojiStatusPicker Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>EmojiStatusPicker</Title>
            <Text size="sm" c="dimmed">
              Modal for picking an emoji and writing a custom status message, with quick
              templates and categorized emoji tabs
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Live example:</Text>
            <Group>
              <Text size="sm">
                Current status: <Text span>{status.emoji}</Text> {status.message}
              </Text>
              <Button variant="light" size="xs" onClick={() => setEmojiOpen(true)}>
                Set status
              </Button>
            </Group>
            <EmojiStatusPicker
              opened={emojiOpen}
              onClose={() => setEmojiOpen(false)}
              onSave={(emoji, message) => setStatus({ emoji, message })}
              currentEmoji={status.emoji}
              currentMessage={status.message}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const [open, setOpen] = useState(false);
const [status, setStatus] = useState({ emoji: '☕', message: 'Coffee break' });

<Button onClick={() => setOpen(true)}>Set status</Button>

<EmojiStatusPicker
  opened={open}
  onClose={() => setOpen(false)}
  onSave={(emoji, message) => setStatus({ emoji, message })}
  currentEmoji={status.emoji}
  currentMessage={status.message}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • opened / onClose - control modal visibility
              <br />
              • onSave(emoji, message) - called when the user saves
              <br />
              • currentEmoji / currentMessage - initial values
              <br />
              • maxLength - max characters for the message (default 100)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* IdleTimeDisplay Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>IdleTimeDisplay</Title>
            <Text size="sm" c="dimmed">
              Renders a relative "last seen" / idle time (e.g. "5m ago") that
              auto-refreshes - pure client-side, no backend
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Examples:</Text>
            <Stack gap="xs">
              <Group gap="xs">
                <Text size="sm" fw={500}>Online:</Text>
                <IdleTimeDisplay lastSeen={new Date()} />
              </Group>
              <Group gap="xs">
                <Text size="sm" fw={500}>Recently:</Text>
                <IdleTimeDisplay lastSeen={fiveMinutesAgo} prefix="Active " />
              </Group>
              <Group gap="xs">
                <Text size="sm" fw={500}>A while ago:</Text>
                <IdleTimeDisplay lastSeen={twoHoursAgo} size="sm" />
              </Group>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<IdleTimeDisplay lastSeen={new Date()} />
<IdleTimeDisplay lastSeen={fiveMinutesAgo} prefix="Active " />
<IdleTimeDisplay lastSeen={twoHoursAgo} size="sm" />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • lastSeen - Date | string | null timestamp
              <br />
              • prefix - optional text prepended to the relative time
              <br />
              • size - 'xs' | 'sm' | 'md' | 'lg'; color - Mantine color
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* PresenceIndicator Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>PresenceIndicator</Title>
            <Text size="sm" c="dimmed">
              Connection-quality badge with color coding and a tooltip for presence /
              websocket state
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>States:</Text>
            <Group>
              <PresenceIndicator isConnected lastConnected={new Date()} />
              <PresenceIndicator
                isConnected={false}
                isReconnecting
                reconnectAttempt={2}
                maxReconnectAttempts={10}
              />
              <PresenceIndicator
                isConnected={false}
                error={new Error('Connection lost')}
              />
            </Group>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Compact (dot only):</Text>
            <Group>
              <PresenceIndicator isConnected showLabel={false} />
              <PresenceIndicator isConnected={false} showLabel={false} />
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<PresenceIndicator isConnected lastConnected={new Date()} />

<PresenceIndicator
  isConnected={false}
  isReconnecting
  reconnectAttempt={2}
  maxReconnectAttempts={10}
/>

<PresenceIndicator isConnected showLabel={false} />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • isConnected - main connection state (green vs red)
              <br />
              • isReconnecting / reconnectAttempt / maxReconnectAttempts - retry status
              <br />
              • lastConnected - Date shown in the tooltip
              <br />
              • error - Error shown inline and in the tooltip
              <br />
              • showLabel - false renders a compact dot only
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* PushSettings Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>PushSettings</Title>
            <Text size="sm" c="dimmed">
              Self-contained panel for managing browser push-notification preferences
              (permission, sound, vibration, preview). Reads/writes local push state and
              uses the browser Notification API - no server calls
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Live example:</Text>
            <PushSettings />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<PushSettings />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • takes no props - manages its own local push state internally
              <br />
              • toggling "Push Notifications" prompts the browser permission dialog
            </Text>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
